import * as angular from "angular"
import { ICropping, INtrReqNutrients, isNumber, sum } from "../omnia"
import { FertiliserAppUnit, IFertiliserAnalysis, IFertiliserStock, IManureStock, INtrReq, INutritionAppQty, INutritionPlanProductApp, ManureAnalysisProvider, NutrientType, ProductAppInput } from "../types"
import { applyMinMaxRates, DefaultNeutralisingValue } from "../utils"
import { getRoundedValueForUnit } from "./units"
import { getManureSpreadableArea } from "@omnia/shared/nutrition"
import { FertiliserLiquidUnit } from "@omnia/shared/generated/enums"

export interface INutritionCalcService {

    getAnalysis(stock: IFertiliserStock | IManureStock): IFertiliserAnalysis

    getProductRateForNutrientRate(nutrientRate: number, application: INutritionPlanProductApp, cropping: ICropping): number;

    calculateClosingRequirements(req: INtrReqNutrients, applications: INutritionPlanProductApp[], crop: ICropping, minIsZero?: boolean): INtrReqNutrients;

    calculateNTotalProvided(req: INtrReq, applications: INutritionPlanProductApp[], cropping: ICropping): number;

    calculateNutrientRate(req: INtrReqNutrients, application: INutritionPlanProductApp, cropping?: ICropping): number
    calculateNutrientRateForNutrient(req: INtrReqNutrients, app: INutritionPlanProductApp, nutrient: NutrientType, cropping?: ICropping): number;
    calculateNutrientRateFromProductRate(productRate: number, application: INutritionPlanProductApp, nutrient: NutrientType, cropping?: ICropping): number

    calculateProductAmount(req: { crop: INtrReq, zones: INtrReq[] }, app: INutritionPlanProductApp, cropping?: ICropping): { amount: number, area: number };
    calculateProductRate(req: INtrReqNutrients, app: INutritionPlanProductApp, cropping?: ICropping, applyLimits?: boolean): number;

}


export function createNutritionCalcService(manureAnalysis: ManureAnalysisProvider): INutritionCalcService {


    return {

        getAnalysis(stock: IFertiliserStock | IManureStock) {
            return getAnalysis(stock)
        },

        getProductRateForNutrientRate,

        calculateNutrientRate,
        calculateNutrientRateForNutrient,
        calculateNutrientRateFromProductRate,

        calculateProductAmount,
        calculateProductRate,

        calculateClosingRequirements,

        calculateNTotalProvided,

    }

    ///

    function calculateClosingRequirements(req: INtrReqNutrients, applications: INutritionPlanProductApp[], cropping: ICropping, minIsZero: boolean = true): INtrReqNutrients {

        if (!req) return req;

        req = angular.copy(req);
        if (!applications.length) return req;

        let reqBase = angular.copy(req);

        if (applications && applications.length > 0) {

            applications = applications.sort((a, b) => a.ApplicationDate.valueOf() - b.ApplicationDate.valueOf());

            applyReqNutrients(req, n => getNumericValue(n));

            applications.forEach(a => {
                let productAnalysis = getAnalysisFromProductApp(a, cropping);
                let productRate = calculateProductRateWithAnalysis(reqBase, a, productAnalysis, cropping);
                applyProduct(req, productRate, a.ProductUnit, productAnalysis);
            });

            applyReqNutrients(req, (n, type) => {
                let d = (type == NutrientType.Lime ? 10 : 1);
                return Math.round(n * d) / d;
            });

            if (minIsZero) {
                applyReqNutrients(req, n => Math.max(0, n));
            }
        }

        return req;
    }

    function calculateNutrientRate(req: INtrReqNutrients, application: INutritionPlanProductApp, cropping?: ICropping): number {

        return calculateNutrientRateForNutrient(req, application, application.TargetNutrient, cropping)

    }

    function calculateNutrientRateForNutrient(req: INtrReqNutrients, application: INutritionPlanProductApp, nutrient: NutrientType, cropping?: ICropping): number {

        const productRate = calculateProductRate(req, application, cropping);
        const productAnalysis = getAnalysisFromProductApp(application, cropping);
        const analysisRate = getTargetNutrientValueFromAnalysis(nutrient, productAnalysis);

        return getNutrientRate(productRate, analysisRate, application)

    }

    function calculateNutrientRateFromProductRate(productRate: number, application: INutritionPlanProductApp, nutrient: NutrientType, cropping?: ICropping): number {

        const productAnalysis = getAnalysisFromProductApp(application, cropping);
        const analysisRate = getTargetNutrientValueFromAnalysis(nutrient, productAnalysis);

        return getNutrientRate(productRate, analysisRate, application)

    }

    function calculateProductAmount(req: { crop: INtrReq, zones: INtrReq[] }, app: INutritionPlanProductApp, cropping?: ICropping) {

        const useZones = (app.LastInputUsed == ProductAppInput.NutrientPct)

        let { amount, area } = useZones
            ? calculateAmountZoneBased(req.zones, app, cropping)
            : calculateAmountCropBased(req.crop, app, cropping);

        let rounding = getAppRounding(app);
        amount = Math.round(amount * rounding) / rounding;

        return { amount, area }

    }


    function calculateAmountCropBased(req: INtrReq, app: INutritionPlanProductApp, cropping?: ICropping) {

        let rate = calculateProductRate(req, app, cropping) || 0;
        let area = getSpreadingArea(app, cropping);

        let amount = area * rate;

        return { amount, area }

    }

    function calculateAmountZoneBased(zones: INtrReq[], app: INutritionPlanProductApp, cropping?: ICropping) {

        let nutrientMask = Math.pow(2, app.TargetNutrient - 1);

        let amounts = zones
            // 210304 - Usersnap 2045
            // Input requirement zones need to be filtered both by nutrient group and nutrient,
            // in case split nutrients (i.e., Terramap) are used
            .filter(req =>
                (req.NutrientGroup & nutrientMask)
                && ((!req.Nutrient) || (req.Nutrient == app.TargetNutrient))
            ).map(req => {

                let rate = calculateProductRate(req, app, cropping) || 0;
                let area = req.Area / 10000;
                let amount = area * rate;

                amount = getRoundedValueForUnit(amount, app.ProductUnit);

                return {
                    amount,
                    rate,
                    area,
                };

            });

        const totalAmount = sum(amounts, a => a.amount);
        const totalArea = sum(amounts, a => a.area)

        return { amount: totalAmount, area: totalArea }

    }

    function calculateProductRate(req: INtrReqNutrients, app: INutritionPlanProductApp, cropping?: ICropping, applyLimits = true): number {

        if (!req || !app) return 0

        const productAnalysis = getAnalysisFromProductApp(app, cropping)

        return calculateProductRateWithAnalysis(req, app, productAnalysis, cropping, applyLimits)

    }


    function getProductRateForNutrientRate(nutrientRate: number, application: INutritionPlanProductApp, cropping: ICropping): number {

        if (!application) return 0

        const productAnalysis = getAnalysisFromProductApp(application, cropping)

        let rate = (application.TargetNutrient === NutrientType.N)
            ? productAnalysis.NTotal
            : getTargetNutrientValueFromAnalysis(application.TargetNutrient, productAnalysis)

        return rate > 0
            ? nutrientRate / rate
            : 0
    }


    function getAnalysis(stock: IFertiliserStock | IManureStock, app?: INutritionPlanProductApp, cropping?: ICropping): IFertiliserAnalysis {

        function isManure(stock: IFertiliserStock | IManureStock): stock is IManureStock {
            const id = stock["ManureStockId"]
            return isNumber(id) && (id > 0)
        }

        if (!stock) {
            return
        }

        const manure = isManure(stock)

        let analysis: IFertiliserAnalysis;

        if (!manure) {

            if (stock.Custom) {
                analysis = stock
            }
            else {
                analysis = stock.StandardProduct
            }

            analysis = {
                ...analysis,
                NTotal: analysis.N
            }

        }
        else {

            analysis = manureAnalysis(stock, app, cropping)

        }

        if (analysis) {
            analysis = normalizeAnalysis(analysis)
        }

        return analysis

    }

    function getAnalysisFromProductApp(a: INutritionPlanProductApp, cropping?: ICropping): IFertiliserAnalysis {

        return getAnalysis(a.Fertiliser ?? a.Manure, a, cropping)

    }

    function calculateNTotalProvided(req: INtrReqNutrients, applications: INutritionPlanProductApp[], cropping: ICropping): number {

        if (!cropping || applications.length === 0) return 0;

        req = angular.copy(req);

        let nAmount = 0;

        applications = applications.sort((a, b) => a.ApplicationDate.valueOf() - b.ApplicationDate.valueOf());

        applyReqNutrients(req, n => getNumericValue(n))

        applications.forEach(a => {

            let productAnalysis = getAnalysisFromProductApp(a, cropping);

            let productRate = calculateProductRateWithAnalysis(req, a, productAnalysis, cropping);

            switch (a.ProductUnit) {

                case FertiliserAppUnit.T:
                case FertiliserAppUnit.M3:
                    productRate *= 1000;
                    break;

            }

            nAmount += (productRate * productAnalysis.NTotal);

        });

        return nAmount;

    }


}

export function getSpreadingArea(application: INutritionPlanProductApp, cropping: ICropping): number {

    if (application.Manure) {
        return getManureSpreadableArea(cropping);
    }

    return cropping.Area;
}


function calculateProductRateWithAnalysis(req: INtrReqNutrients, app: INutritionPlanProductApp, analysis: IFertiliserAnalysis, cropping: ICropping, applyLimits = true): number {

    let { productRate, nutrientRate, nutrientPct, lastInput, rounding } = getApplicationInfo(app, cropping);

    // If we use product rate calculation
    if (lastInput === ProductAppInput.ProductRate) {
        return productRate;
    }

    // If we use nutrient % calculation
    if (lastInput === ProductAppInput.NutrientPct) {
        var analysisRate = getTargetNutrientValueFromAnalysis(app.TargetNutrient, analysis);
        var croppingRate = getTargetNutrientValueFromRequirement(app.TargetNutrient, req);
        productRate = ((analysisRate > 0) && (croppingRate > 0) ? croppingRate / analysisRate : 0) * nutrientPct;
    }
    // If we use nutrient rate calculation
    else if (lastInput === ProductAppInput.NutrientRate) {
        var analysisRate = getTargetNutrientValueFromAnalysis(app.TargetNutrient, analysis);
        productRate = analysisRate > 0 ? nutrientRate / analysisRate : 0;
    }

    switch (app.ProductUnit) {

        case FertiliserAppUnit.T:
        case FertiliserAppUnit.M3:
            productRate /= 1000;
            break;

    }

    if (rounding) {
        productRate = Math.round(productRate * rounding) / rounding;
    }

    applyLimits = applyLimits && ((lastInput == ProductAppInput.NutrientPct) || (!!app.ManureId))

    if (applyLimits) {
        productRate = applyMinMaxRates(productRate, app)
    }

    return productRate;

}


function getApplicationInfo(app: INutritionPlanProductApp, cropping: { CroppingId: number }) {

    let qty: INutritionAppQty = app;

    if (cropping && app.Cropping) {
        let override = app.Cropping.find(c => c.CroppingId == cropping.CroppingId);
        qty = override || app;
    }

    let rounding = getAppRounding(app);

    if (qty) {
        return {
            productRate: qty.ProductRate,
            nutrientRate: qty.NutrientRate,
            nutrientPct: qty.NutrientPct,
            lastInput: qty.LastInputUsed,
            rounding: rounding
        }
    }
}

function applyProduct(req: INtrReqNutrients, productRate: number, unit: FertiliserAppUnit, productAnalysis: IFertiliserAnalysis) {

    let multiplier = 1;

    // If the product rate is expressed in T/ha or m3/ha, it must be multiplied by 1000 to match the requirements
    switch (unit) {
        case FertiliserAppUnit.T:
        case FertiliserAppUnit.M3:
            multiplier = 1000;
            break;
    }

    req.RequiredN -= Math.round(multiplier * productRate * productAnalysis.N);
    req.RequiredSO3 -= Math.round(multiplier * productRate * productAnalysis.SO3);
    req.RequiredP2O5 -= Math.round(multiplier * productRate * productAnalysis.P2O5);
    req.RequiredK2O -= Math.round(multiplier * productRate * productAnalysis.K2O);
    req.RequiredMgO -= Math.round(multiplier * productRate * productAnalysis.MgO);

    req.RequiredLime -= Math.round(productRate * ((productAnalysis.NeutralisingValue || 0) / DefaultNeutralisingValue));

}

function applyReqNutrients(req: INtrReqNutrients, f: (value: number, type: NutrientType) => number) {
    req.RequiredN = f(req.RequiredN, NutrientType.N);
    req.RequiredSO3 = f(req.RequiredSO3, NutrientType.SO3);
    req.RequiredP2O5 = f(req.RequiredP2O5, NutrientType.P2O5);
    req.RequiredK2O = f(req.RequiredK2O, NutrientType.K2O);
    req.RequiredMgO = f(req.RequiredMgO, NutrientType.MgO);
    req.RequiredLime = f(req.RequiredLime, NutrientType.Lime);
}


export function normalizeAnalysis(analysis: IFertiliserAnalysis): IFertiliserAnalysis {

    if (analysis) {

        analysis = angular.copy(analysis);

        var nutrientConversion = (n: number) => n;

        if (analysis.Liquid && (analysis.LiquidUnit == FertiliserLiquidUnit.WeightForWeight)) {

            nutrientConversion = (n: number) => n * analysis.SpecificGravity;

        }

        analysis.N = nutrientConversion(getNumericValue(analysis.N));
        analysis.NTotal = nutrientConversion(getNumericValue(analysis.NTotal));
        analysis.P2O5 = nutrientConversion(getNumericValue(analysis.P2O5));
        analysis.SO3 = nutrientConversion(getNumericValue(analysis.SO3));
        analysis.MgO = nutrientConversion(getNumericValue(analysis.MgO));
        analysis.K2O = nutrientConversion(getNumericValue(analysis.K2O));
        analysis.Lime = getNumericValue(analysis.Lime);

    }

    return analysis;
}

function getNumericValue(value: number): number {
    if (value && angular.isNumber(value)) {
        return value;
    }
    return 0;
}

export function getTargetNutrientValueFromAnalysis(target: NutrientType, analysis: IFertiliserAnalysis): number {

    switch (target) {

        case NutrientType.N:
            return analysis.N;

        case NutrientType.SO3:
            return analysis.SO3;

        case NutrientType.P2O5:
            return analysis.P2O5;

        case NutrientType.K2O:
            return analysis.K2O;

        case NutrientType.MgO:
            return analysis.MgO;

        case NutrientType.Lime:
            return analysis.Lime;

    }

    return 0;

}

export function getTargetNutrientValueFromRequirement(target: NutrientType, requirement: INtrReqNutrients): number {

    switch (target) {

        case NutrientType.N:
            return getNumericValue(requirement.RequiredN);

        case NutrientType.SO3:
            return getNumericValue(requirement.RequiredSO3);

        case NutrientType.P2O5:
            return getNumericValue(requirement.RequiredP2O5);

        case NutrientType.K2O:
            return getNumericValue(requirement.RequiredK2O);

        case NutrientType.MgO:
            return getNumericValue(requirement.RequiredMgO);

        case NutrientType.Lime:
            return getNumericValue(requirement.RequiredLime);

    }

    return 0;
}

export function getAppRounding(app: INutritionPlanProductApp): number {

    let rounding = 1;

    switch (app.ProductUnit) {
        case FertiliserAppUnit.M3:
        case FertiliserAppUnit.T:
            rounding = 10;
            break;
    }

    return rounding;
}


function getNutrientRate(productRate: number, analysis: number, app: INutritionPlanProductApp) {

    const unit = app.ProductUnit

    const f = (unit == FertiliserAppUnit.T) || (unit === FertiliserAppUnit.M3)
        ? 1000
        : 1

    const nutrientRate = productRate * analysis * f

    return nutrientRate
}
